import React, { useEffect, useState } from 'react';

import { useTranslation } from 'src/i18n';
import { Button } from 'src/component/ds/ui/button';
import { SidebarMenu, SidebarMenuItem, SidebarMenuSkeleton } from 'src/component/ds/ui/sidebar';
import type { DeviceNavNode as DeviceNavNodeData } from 'src/service/api/deviceNav';
import type { DeviceNavSelection } from './deviceNavState';
import useDeviceNav from './useDeviceNav';
import DeviceNavAllItem from './DeviceNavAllItem';
import DeviceNavNode from './DeviceNavNode';

const SKELETON_ROWS = 8;

interface DeviceNavTreeProps {
  selection: DeviceNavSelection;
  onSelect: (selection: DeviceNavSelection) => void;
  onAdd?: () => void;
}

export default function DeviceNavTree ({ selection, onSelect, onAdd }: DeviceNavTreeProps) {
  const { t } = useTranslation();
  const { tab, nodeId, childId } = selection;
  const { items, total, loading, error, retry } = useDeviceNav(tab);

  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(nodeId ? [nodeId] : []));

  // A row selected from the URL is visible on arrival.
  useEffect(() => {
    if (nodeId && childId) {
      setExpanded((current) => (current.has(nodeId) ? current : new Set(current).add(nodeId)));
    }
  }, [nodeId, childId]);

  const toggle = (id: string, open: boolean) => setExpanded((current) => {
    const next = new Set(current);
    if (open) next.add(id); else next.delete(id);
    return next;
  });

  const nodeLabel = (node: DeviceNavNodeData) => node.tag ?? t(`deviceNav.unassigned.${tab}`);

  if (error && !items.length) {
    return (
      <div className='ds-flex ds-flex-col ds-items-start ds-gap-2 ds-px-3 ds-py-2 ds-text-sm ds-text-sidebar-foreground'>
        <p>{t('deviceNav.loadFailed')}</p>
        <Button variant='outline' size='sm' onClick={retry}>{t('deviceNav.retry')}</Button>
      </div>
    );
  }

  return (
    <SidebarMenu className='ds-gap-0' aria-busy={loading}>
      {loading && !items.length ? (
        Array.from({ length: SKELETON_ROWS }, (_, index) => (
          <SidebarMenuItem key={index}>
            <SidebarMenuSkeleton className='ds-h-[38px] ds-px-3' />
          </SidebarMenuItem>
        ))
      ) : (
        <>
          <DeviceNavAllItem
            label={t(`deviceNav.all.${tab}`)}
            addLabel={t(`deviceNav.add.${tab}`)}
            total={total}
            active={!nodeId}
            onSelect={() => onSelect({ tab })}
            onAdd={onAdd}
          />
          {items.map((node) => (
            <DeviceNavNode
              key={node.id}
              node={node}
              label={nodeLabel(node)}
              expanded={expanded.has(node.id)}
              onExpandedChange={(open) => toggle(node.id, open)}
              active={node.id === nodeId && !childId}
              activeChildId={node.id === nodeId ? childId : undefined}
              onSelect={() => onSelect({ tab, nodeId: node.id })}
              onSelectChild={(id) => onSelect({ tab, nodeId: node.id, childId: id })}
            />
          ))}
          {!items.length && (
            <li className='ds-px-3 ds-py-2 ds-text-sm ds-text-muted-foreground'>{t(`deviceNav.empty.${tab}`)}</li>
          )}
        </>
      )}
    </SidebarMenu>
  );
}
